import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSiteSettingsService } from '../../../services/adminSiteSettingsService';
import { Save, Plus, Trash2, ListOrdered } from 'lucide-react';
import { Button, Input, TextArea } from '../../shared';
import { useToastContext } from '../../../contexts/ToastContext';

const DEFAULT_HOW_IT_WORKS = {
  hero: {
    badge: 'Simple & Easy Process',
    title: 'How It Works',
    subtitle: "Planning your perfect event is simple with our streamlined process. From browsing vendors to enjoying your event, we've made it easy every step of the way.",
    primaryButtonText: 'Start Browsing',
    primaryButtonLink: '/browse',
    secondaryButtonText: 'Become a Vendor',
    secondaryButtonLink: '/register',
  },
  steps: [
    { number: '01', icon: 'Search', title: 'Browse & Search', description: 'Explore our extensive directory of verified vendors. Filter by category, location, price range, and ratings to find the perfect match for your event.', color: 'from-blue-500 to-blue-600' },
    { number: '02', icon: 'Calendar', title: 'Compare & Select', description: 'View detailed profiles, portfolios, reviews, and pricing. Compare multiple vendors side-by-side to make an informed decision.', color: 'from-purple-500 to-purple-600' },
    { number: '03', icon: 'CreditCard', title: 'Book & Pay', description: 'Secure your booking with our safe payment system. Choose your preferred date, customize your package, and complete payment in minutes.', color: 'from-green-500 to-green-600' },
    { number: '04', icon: 'CheckCircle', title: 'Enjoy Your Event', description: 'Receive instant confirmation and stay connected with your vendor. On event day, sit back and enjoy while professionals handle everything.', color: 'from-orange-500 to-orange-600' },
  ],
  features: {
    title: 'Why Choose Our Platform',
    subtitle: "We've built a platform that makes event planning simple, secure, and enjoyable.",
    items: [
      { icon: 'Shield', title: 'Verified Vendors', description: 'All vendors undergo thorough verification and background checks to ensure quality and reliability.' },
      { icon: 'Star', title: 'Real Reviews', description: 'Read authentic reviews from previous clients to make confident decisions.' },
      { icon: 'CreditCard', title: 'Secure Payments', description: 'Your payments are protected with industry-standard encryption and secure processing.' },
      { icon: 'MessageSquare', title: 'Direct Communication', description: 'Chat directly with vendors to discuss details, ask questions, and customize your booking.' },
      { icon: 'Calendar', title: 'Easy Scheduling', description: 'Check availability in real-time and book your preferred date instantly.' },
      { icon: 'Users', title: '24/7 Support', description: 'Our support team is available around the clock to assist you with any questions or concerns.' },
    ],
  },
  cta: {
    title: 'Ready to Plan Your Event?',
    subtitle: 'Join thousands of satisfied clients who have found their perfect vendors through our platform.',
    primaryButtonText: 'Browse Vendors',
    primaryButtonLink: '/browse',
    secondaryButtonText: 'Create Account',
    secondaryButtonLink: '/register',
  },
};

const STEP_COLORS = [
  'from-blue-500 to-blue-600',
  'from-purple-500 to-purple-600',
  'from-green-500 to-green-600',
  'from-orange-500 to-orange-600',
  'from-pink-500 to-pink-600',
  'from-cyan-500 to-cyan-600',
];

const HowItWorksSettings = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [formData, setFormData] = useState(DEFAULT_HOW_IT_WORKS);

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['adminSiteSettings'],
    queryFn: () => adminSiteSettingsService.get(),
  });

  useEffect(() => {
    const hiw = settingsResponse?.data?.how_it_works;
    if (hiw && typeof hiw === 'object') {
      setFormData({
        hero: { ...DEFAULT_HOW_IT_WORKS.hero, ...hiw.hero },
        steps: Array.isArray(hiw.steps) && hiw.steps.length > 0 ? hiw.steps : DEFAULT_HOW_IT_WORKS.steps,
        features: { ...DEFAULT_HOW_IT_WORKS.features, ...hiw.features, items: hiw.features?.items?.length ? hiw.features.items : DEFAULT_HOW_IT_WORKS.features.items },
        cta: { ...DEFAULT_HOW_IT_WORKS.cta, ...hiw.cta },
      });
    }
  }, [settingsResponse]);

  const saveMutation = useMutation({
    mutationFn: (data) => adminSiteSettingsService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSiteSettings']);
      queryClient.invalidateQueries(['siteSettings']);
      toast.success(null, 'How It Works saved successfully');
    },
    onError: (error) => {
      toast.error(null, error.response?.data?.message || 'Failed to save');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullSettings = { ...settingsResponse?.data, how_it_works: formData };
    saveMutation.mutate(fullSettings);
  };

  const updateHero = (field, value) => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, [field]: value } }));
  const updateCta = (field, value) => setFormData((prev) => ({ ...prev, cta: { ...prev.cta, [field]: value } }));
  const updateFeatures = (field, value) => setFormData((prev) => ({ ...prev, features: { ...prev.features, [field]: value } }));
  const updateFeatureItem = (index, field, value) => {
    setFormData((prev) => {
      const items = [...(prev.features.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, features: { ...prev.features, items } };
    });
  };
  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, items: [...(prev.features.items || []), { icon: 'Star', title: '', description: '' }] },
    }));
  };
  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, items: prev.features.items.filter((_, i) => i !== index) },
    }));
  };
  const updateStep = (index, field, value) => {
    setFormData((prev) => {
      const steps = [...prev.steps];
      steps[index] = { ...steps[index], [field]: value };
      return { ...prev, steps };
    });
  };
  const addStep = () => {
    const num = String((formData.steps?.length || 0) + 1).padStart(2, '0');
    setFormData((prev) => ({
      ...prev,
      steps: [...(prev.steps || []), { number: num, icon: 'Star', title: '', description: '', color: STEP_COLORS[(prev.steps?.length || 0) % STEP_COLORS.length] }],
    }));
  };
  const removeStep = (index) => {
    setFormData((prev) => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <ListOrdered className="w-5 h-5" />
            How It Works Page
          </h2>
          <p className="text-sm text-gray-600">Configure the content for the public How It Works page.</p>
        </div>

        {/* Hero Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Hero Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
              <Input value={formData.hero?.badge || ''} onChange={(e) => updateHero('badge', e.target.value)} placeholder="Simple & Easy Process" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <Input value={formData.hero?.title || ''} onChange={(e) => updateHero('title', e.target.value)} placeholder="How It Works" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <TextArea value={formData.hero?.subtitle || ''} onChange={(e) => updateHero('subtitle', e.target.value)} rows={3} placeholder="Planning your perfect event..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Text</label>
                <Input value={formData.hero?.primaryButtonText || ''} onChange={(e) => updateHero('primaryButtonText', e.target.value)} placeholder="Start Browsing" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Link</label>
                <Input value={formData.hero?.primaryButtonLink || ''} onChange={(e) => updateHero('primaryButtonLink', e.target.value)} placeholder="/browse" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Text</label>
                <Input value={formData.hero?.secondaryButtonText || ''} onChange={(e) => updateHero('secondaryButtonText', e.target.value)} placeholder="Become a Vendor" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Link</label>
                <Input value={formData.hero?.secondaryButtonLink || ''} onChange={(e) => updateHero('secondaryButtonLink', e.target.value)} placeholder="/register" />
              </div>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Steps</h3>
            <Button type="button" variant="outline" size="sm" onClick={addStep}>
              <Plus className="w-4 h-4 mr-1" /> Add Step
            </Button>
          </div>
          <div className="space-y-4">
            {(formData.steps || []).map((step, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Step {i + 1}</span>
                  <button type="button" onClick={() => removeStep(i)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="Number" value={step.number || ''} onChange={(e) => updateStep(i, 'number', e.target.value)} placeholder="01" />
                  <Input label="Icon (e.g. Search, Calendar)" value={step.icon || ''} onChange={(e) => updateStep(i, 'icon', e.target.value)} />
                  <Input label="Title" value={step.title || ''} onChange={(e) => updateStep(i, 'title', e.target.value)} className="md:col-span-2" />
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <TextArea value={step.description || ''} onChange={(e) => updateStep(i, 'description', e.target.value)} rows={2} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color (Tailwind gradient)</label>
                    <select
                      value={step.color || STEP_COLORS[i % STEP_COLORS.length]}
                      onChange={(e) => updateStep(i, 'color', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {STEP_COLORS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Features Section</h3>
            <Button type="button" variant="outline" size="sm" onClick={addFeature}>
              <Plus className="w-4 h-4 mr-1" /> Add Feature
            </Button>
          </div>
          <div className="space-y-4 mb-4">
            <Input label="Section Title" value={formData.features?.title || ''} onChange={(e) => updateFeatures('title', e.target.value)} placeholder="Why Choose Our Platform" />
            <Input label="Section Subtitle" value={formData.features?.subtitle || ''} onChange={(e) => updateFeatures('subtitle', e.target.value)} placeholder="We've built a platform..." />
          </div>
          <div className="space-y-4">
            {(formData.features?.items || []).map((item, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Feature {i + 1}</span>
                  <button type="button" onClick={() => removeFeature(i)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="Icon" value={item.icon || ''} onChange={(e) => updateFeatureItem(i, 'icon', e.target.value)} placeholder="Shield" />
                  <Input label="Title" value={item.title || ''} onChange={(e) => updateFeatureItem(i, 'title', e.target.value)} />
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <TextArea value={item.description || ''} onChange={(e) => updateFeatureItem(i, 'description', e.target.value)} rows={2} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">CTA Section</h3>
          <div className="space-y-4">
            <Input label="Title" value={formData.cta?.title || ''} onChange={(e) => updateCta('title', e.target.value)} placeholder="Ready to Plan Your Event?" />
            <TextArea label="Subtitle" value={formData.cta?.subtitle || ''} onChange={(e) => updateCta('subtitle', e.target.value)} rows={2} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Primary Button Text" value={formData.cta?.primaryButtonText || ''} onChange={(e) => updateCta('primaryButtonText', e.target.value)} />
              <Input label="Primary Button Link" value={formData.cta?.primaryButtonLink || ''} onChange={(e) => updateCta('primaryButtonLink', e.target.value)} />
              <Input label="Secondary Button Text" value={formData.cta?.secondaryButtonText || ''} onChange={(e) => updateCta('secondaryButtonText', e.target.value)} />
              <Input label="Secondary Button Link" value={formData.cta?.secondaryButtonLink || ''} onChange={(e) => updateCta('secondaryButtonLink', e.target.value)} />
            </div>
          </div>
        </div>

        <Button type="submit" disabled={saveMutation.isPending}>
          <Save className="w-4 h-4 mr-2" />
          Save How It Works
        </Button>
      </form>
    </div>
  );
};

export default HowItWorksSettings;
